if (!customElements.get("iyrs-shipping-urgency")) {
  customElements.define(
    "iyrs-shipping-urgency",
    class IyrsShippingUrgency extends HTMLElement {
      connectedCallback() {
        this.timeZone = this.dataset.timeZone || "America/New_York";
        this.cutoffHour = Number(this.dataset.cutoffHour || 14);
        this.cutoffMinute = Number(this.dataset.cutoffMinute || 0);
        this.shippingRow = this.querySelector("[data-iyrs-shipping-row]");
        this.shippingLabel = this.querySelector("[data-iyrs-shipping-label]");
        this.deliveryLabel = this.querySelector("[data-iyrs-delivery-label]");
        this.scarcityRow = this.querySelector("[data-iyrs-scarcity-row]");
        this.availabilityLabel = this.querySelector("[data-iyrs-availability-label]");
        this.variants = JSON.parse(this.querySelector("[data-iyrs-inventory-map]")?.textContent || "[]");
        this.onVariantChanged = (event) => this.updateScarcity(event.detail?.variant?.id);

        this.updateTime();
        this.updateDelivery();
        this.updateScarcity(this.selectedVariantId());
        if (this.dataset.enableCountdown === "true") this.timer = window.setInterval(() => this.updateTime(), 1000);
        document.addEventListener("variant:changed", this.onVariantChanged);
      }

      disconnectedCallback() {
        window.clearInterval(this.timer);
        document.removeEventListener("variant:changed", this.onVariantChanged);
      }

      easternParts(date = new Date()) {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: this.timeZone,
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          hourCycle: "h23",
        });
        return Object.fromEntries(
          formatter.formatToParts(date).filter(({ type }) => type !== "literal").map(({ type, value }) => [type, Number(value)])
        );
      }

      dateFromParts(parts) {
        return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
      }

      addBusinessDays(date, count) {
        const result = new Date(date);
        let added = 0;
        while (added < count) {
          result.setUTCDate(result.getUTCDate() + 1);
          const day = result.getUTCDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return result;
      }

      updateTime() {
        if (this.dataset.enableCountdown !== "true" || !this.shippingLabel) return;
        const now = this.easternParts();
        const today = this.dateFromParts(now);
        const weekday = today.getUTCDay();
        const cutoffSeconds = this.cutoffHour * 3600 + this.cutoffMinute * 60;
        const nowSeconds = now.hour * 3600 + now.minute * 60 + now.second;

        if (weekday !== 0 && weekday !== 6 && nowSeconds < cutoffSeconds) {
          const remaining = cutoffSeconds - nowSeconds;
          const hours = String(Math.floor(remaining / 3600)).padStart(2, "0");
          const minutes = String(Math.floor((remaining % 3600) / 60)).padStart(2, "0");
          const seconds = String(remaining % 60).padStart(2, "0");
          const countdown = document.createElement("strong");
          countdown.textContent = `${hours}:${minutes}:${seconds}`;
          this.shippingLabel.replaceChildren("Order within ", countdown, " to ship TODAY!");
          return;
        }

        const nextBusiness = this.addBusinessDays(today, 1);
        const tomorrow = new Date(today);
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        const nextLabel = nextBusiness.getTime() === tomorrow.getTime()
          ? "tomorrow morning"
          : `${new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone: "UTC" }).format(nextBusiness)} morning`;
        this.shippingLabel.textContent = `Ships ${nextLabel}!`;
      }

      updateDelivery() {
        if (!this.deliveryLabel) return;
        this.deliveryLabel.textContent = "See checkout for estimated delivery dates and shipping options.";
      }

      selectedVariantId() {
        const productInfo = this.closest("product-info");
        const value = productInfo?.querySelector(".m-product-form--main [name='id']")?.value;
        return value ? Number(value) : this.variants[0]?.id;
      }

      inventoryMessage(variant) {
        if (!variant) return "Select an available option";
        if (variant.available === false) return "Sold out";
        if (variant.available !== true) return "";

        // An untracked zero is not a stock count. Availability remains Shopify's decision.
        const quantity = variant.inventoryQuantity;
        const hasQuantity = Boolean(variant.inventoryManagement)
          && Number.isInteger(quantity) && quantity > 0;
        if (!hasQuantity) return "Available to order";

        const threshold = Number(this.dataset.inventoryThreshold || 6);
        const showRealLowStock = this.dataset.showScarcity === "true"
          && variant.inventoryPolicy === "deny"
          && Number.isInteger(threshold) && threshold > 0 && quantity <= threshold;
        return showRealLowStock ? `Only ${quantity} left` : "In stock";
      }

      updateScarcity(variantId) {
        if (!this.scarcityRow || !this.availabilityLabel) return;
        const variant = this.variants.find(({ id }) => Number(id) === Number(variantId));
        const message = this.inventoryMessage(variant);
        // Never leave a stale number in hidden markup or rely on CSS for product truth.
        this.availabilityLabel.textContent = message;
        this.scarcityRow.hidden = !message;
      }
    }
  );
}

if (!customElements.get("iyrs-cap-guide")) {
  customElements.define(
    "iyrs-cap-guide",
    class IyrsCapGuide extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector("dialog");
        if (!this.dialog) return;
        this.triggers = document.querySelectorAll(`[data-iyrs-cap-open="${this.dialog.id}"]`);
        this.tabs = this.querySelectorAll("[data-iyrs-cap-step]");
        this.panels = this.querySelectorAll("[data-iyrs-cap-panel]");
        this.returnFocus = null;

        this.triggers.forEach((trigger) => trigger.addEventListener("click", () => {
          this.returnFocus = trigger;
          this.activate("circumference");
          this.dialog.showModal();
        }));
        this.querySelector("[data-iyrs-cap-close]")?.addEventListener("click", () => this.dialog.close());
        this.tabs.forEach((tab) => tab.addEventListener("click", () => this.activate(tab.dataset.iyrsCapStep)));
        this.dialog.addEventListener("click", (event) => {
          if (event.target === this.dialog) this.dialog.close();
        });
        this.dialog.addEventListener("close", () => this.returnFocus?.focus());
      }

      activate(step) {
        this.dialog.dataset.step = step;
        this.tabs.forEach((tab) => tab.setAttribute("aria-selected", String(tab.dataset.iyrsCapStep === step)));
        this.panels.forEach((panel) => { panel.hidden = panel.dataset.iyrsCapPanel !== step; });
      }
    }
  );
}
